"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { AddPaymentOffcanvas } from "./add"
import { EditPaymentOffcanvas } from "./edit"
import { PaymentsList, type PaymentFilters } from "./list"
import api from "../../../libs/axios"
import { TrainingPayment, TrainingType, TrainingInstitute, Pagination, TrainingRequest } from "../types"
import type { SelectOption } from "../../../utils/select-with-search"

export const TrainingPaymentsManagement: React.FC = () => {
  const [payments, setPayments] = useState<TrainingPayment[]>([])
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([])
  const [institutes, setInstitutes] = useState<TrainingInstitute[]>([])
  const [trainingRequests, setTrainingRequests] = useState<TrainingRequest[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0, limit: 10, page: 1, total_pages: 1, total_count: 0,
    has_next_page: false, has_prev_page: false, next_page: null, prev_page: null,
  })

  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<TrainingPayment | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [typeFilter, setTypeFilter] = useState<string>("")
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [advancedFilters, setAdvancedFilters] = useState<PaymentFilters>({})

  const fetchPayments = async (
    status = "",
    paymentType = "",
    page = 1,
    limitValue?: number,
    sort = sortConfig,
    filters = advancedFilters,
  ) => {
    setLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10
      const queryParams: any = { page, limit: effectiveLimit }
      if (status) queryParams.payment_status = status
      if (paymentType) queryParams.payment_type = paymentType
      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (key.endsWith("_label")) return
        if (value !== undefined && value !== "") {
          queryParams[key] = value
        }
      })

      const response = await api.get("/training/payments/list", { params: queryParams })
      if (response.data.success) {
        const paymentData =
          response.data.data?.payments ??
          response.data.data?.training_payments ??
          []
        setPayments(paymentData)
        setPagination(response.data.data.pagination || pagination)
      }
    } catch (error) {
      console.error("Error fetching payments:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDropdowns = async () => {
    const [typesResult, institutesResult, requestsResult] = await Promise.allSettled([
      api.get("/training/courses/active"),
      api.get("/training/institutes/active"),
      api.get("/training/requests/list", { params: { page: 1, limit: 100, status: "scheduled" } }),
    ])
    if (typesResult.status === "fulfilled" && typesResult.value.data.success) {
      const typesData =
        typesResult.value.data.data?.training_types ??
        typesResult.value.data.data?.types ??
        []
      setTrainingTypes(typesData)
    }
    if (institutesResult.status === "fulfilled" && institutesResult.value.data.success) {
      setInstitutes(institutesResult.value.data.data.institutes || [])
    }
    if (requestsResult.status === "fulfilled" && requestsResult.value.data.success) {
      const requestData =
        requestsResult.value.data.data?.training_requests ??
        requestsResult.value.data.data?.requests ??
        []
      setTrainingRequests(requestData)
    }
  }

  const createPayment = async (data: any) => {
    setActionLoading(true)
    const paymentDocuments = data._payment_documents || []
    const { _payment_documents, ...submitData } = data
    try {
      const response = await api.post("/training/payments/create", submitData)
      if (response.data.success) {
        const paymentId = response.data.data?.training_payment?.id || response.data.data?.payment?.id
        if (paymentId && paymentDocuments.length > 0) {
          await uploadPaymentDocuments(paymentId, paymentDocuments)
        }
        await fetchPayments(statusFilter, typeFilter, 1, undefined, sortConfig, advancedFilters)
        setShowAdd(false)
      }
    } catch (error) {
      console.error("Error creating payment:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updatePayment = async (data: any) => {
    setActionLoading(true)
    const paymentDocuments = data._payment_documents || []
    const { _payment_documents, ...submitData } = data
    try {
      const response = await api.put(`/training/payments/${data.id}`, submitData)
      if (response.data.success) {
        if (paymentDocuments.length > 0) {
          await uploadPaymentDocuments(data.id, paymentDocuments)
        }
        await fetchPayments(statusFilter, typeFilter, pagination.page, undefined, sortConfig, advancedFilters)
        setShowEdit(false)
        setSelectedPayment(null)
      }
    } catch (error) {
      console.error("Error updating payment:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const uploadPaymentDocuments = async (
    paymentId: number,
    documents: Array<{ file: File; document_type: string }>
  ) => {
    await Promise.all(documents.map(({ file, document_type }) => {
      const formData = new FormData()
      formData.append("documents", file)
      formData.append("document_type", document_type)
      formData.append("document_name", file.name)
      return api.post(`/training/payments/${paymentId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    }))
  }

  const markPaid = async (id: number, paymentReference: string, paymentDate?: string) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/training/payments/${id}/mark-paid`, { payment_reference: paymentReference, payment_date: paymentDate })
      if (response.data.success) {
        await fetchPayments(statusFilter, typeFilter, pagination.page, undefined, sortConfig, advancedFilters)
      }
    } catch (error) {
      console.error("Error marking payment:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleStatusFilter = (status: string) => { setStatusFilter(status); fetchPayments(status, typeFilter, 1, undefined, sortConfig, advancedFilters) }
  const handleTypeFilter = (type: string) => { setTypeFilter(type); fetchPayments(statusFilter, type, 1, undefined, sortConfig, advancedFilters) }
  const handlePageChange = (page: number) => { fetchPayments(statusFilter, typeFilter, page, undefined, sortConfig, advancedFilters) }
  const handleLimitChange = (limit: number) => { setPagination(prev => ({ ...prev, limit, page: 1 })); fetchPayments(statusFilter, typeFilter, 1, limit, sortConfig, advancedFilters) }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchPayments(statusFilter, typeFilter, pagination.page, undefined, sort, advancedFilters)
  }

  const handleAdvancedFilter = (filters: PaymentFilters) => {
    setAdvancedFilters(filters)
    fetchPayments(statusFilter, typeFilter, 1, undefined, sortConfig, filters)
  }

  const handleEdit = (payment: TrainingPayment) => { setSelectedPayment(payment); setShowEdit(true) }
  const handleCloseModals = () => { setShowAdd(false); setShowEdit(false); setSelectedPayment(null) }

  useEffect(() => { fetchPayments(); fetchDropdowns() }, [])

  const trainingTypeOptions = useMemo<SelectOption[]>(() => (
    trainingTypes.map((type) => ({ id: type.id, value: type.id, label: type.training_name }))
  ), [trainingTypes])

  const trainingInstituteOptions = useMemo<SelectOption[]>(() => (
    institutes.map((institute) => ({ id: institute.id, value: institute.id, label: institute.institute_name }))
  ), [institutes])

  const trainingRequestOptions = useMemo<SelectOption[]>(() => (
    trainingRequests.map((request) => ({
      id: request.id,
      value: request.id,
      label: request.request_id
        ? `${request.request_id}${(request as any).candidate_name ? ` - ${(request as any).candidate_name}` : ""}`
        : `Request #${request.id}`,
    }))
  ), [trainingRequests])

  return (
    <div>
      <PaymentsList
        payments={payments} pagination={pagination} onStatusFilter={handleStatusFilter} onTypeFilter={handleTypeFilter}
        onPageChange={handlePageChange} onLimitChange={handleLimitChange} onAdd={() => setShowAdd(true)}
        onEdit={handleEdit} onMarkPaid={markPaid} loading={loading} sortConfig={sortConfig} onSort={handleSort}
        onAdvancedFilter={handleAdvancedFilter} advancedFilters={advancedFilters}
        trainingTypeOptions={trainingTypeOptions} trainingInstituteOptions={trainingInstituteOptions}
      />
      <AddPaymentOffcanvas
        isOpen={showAdd}
        onClose={handleCloseModals}
        onSubmit={createPayment}
        trainingTypes={trainingTypes}
        institutes={institutes}
        trainingRequests={trainingRequests}
        loading={actionLoading}
      />
      <EditPaymentOffcanvas isOpen={showEdit} onClose={handleCloseModals} onSubmit={updatePayment} payment={selectedPayment} loading={actionLoading} />
    </div>
  )
}
